import { useEffect } from "react";
import { SITE_URL, DEFAULT_OG_IMAGE } from "@/config/seo";

// Imperatively syncs <head> tags for the current route. React 19's native
// metadata does not dedupe against index.html, so we update the existing tags
// in place (creating them if missing) to keep a single source of truth.
const upsertMeta = (matchAttr, matchValue, content) => {
  let el = document.head.querySelector(`meta[${matchAttr}="${matchValue}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(matchAttr, matchValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const Seo = ({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  keywords,
  noindex = false,
  jsonLd = [],
}) => {
  const jsonLdKey = JSON.stringify(jsonLd);

  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    document.title = title;

    upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta(
      "name",
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );
    upsertCanonical(url);

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);

    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    // Managed route-level JSON-LD (global schema in index.html is left untouched)
    document.head.querySelectorAll('script[data-seo="route"]').forEach((n) => n.remove());
    jsonLd.forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo", "route");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
    });

    return () => {
      document.head.querySelectorAll('script[data-seo="route"]').forEach((n) => n.remove());
    };
  }, [title, description, path, image, type, keywords, noindex, jsonLdKey]);

  return null;
};
